import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  BulkImportEditRow,
  BulkImportResult,
  BulkImportReview,
  BulkImportSetup,
} from './components';
import { useBulkUserImport } from './hooks';
import { getBulkUserTypeLabel } from './utils';

interface BulkUserImportProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function BulkUserImport({ isOpen, onClose, onSuccess }: BulkUserImportProps) {
  const [isLeaveConfirmOpen, setIsLeaveConfirmOpen] = useState(false);
  const importState = useBulkUserImport(onSuccess);
  const {
    step,
    userType,
    selectedBrandIds,
    setSelectedBrandIds,
    file,
    fileError,
    setupError,
    rows,
    result,
    previewBrands,
    brandOptions,
    partnersLoading,
    partnersError,
    summary,
    isTypeChangeConfirmOpen,
    isConfirmOpen,
    setIsConfirmOpen,
    rowPendingRemoval,
    editingRow,
    setEditingRow,
    handleUserTypeChange,
    confirmUserTypeChange,
    cancelUserTypeChange,
    handleFileChange,
    handlePreview,
    handleEditRow,
    requestRemoveRow,
    confirmRemoveRow,
    cancelRemoveRow,
    handleConfirmImport,
    startNewImport,
    goBackToSetup,
    resetImportState,
  } = importState;

  const hasUnsavedWork = rows.length > 0 || !!file || step === 'review' || step === 'confirming';
  const displayedBrands =
    previewBrands.length > 0
      ? previewBrands
      : brandOptions.filter((brand) => selectedBrandIds.includes(brand.id));

  const closeFlow = () => {
    resetImportState();
    setIsLeaveConfirmOpen(false);
    onClose();
  };

  const requestClose = () => {
    if (step === 'processing' || step === 'confirming') return;
    if (hasUnsavedWork && step !== 'result') {
      setIsLeaveConfirmOpen(true);
      return;
    }
    closeFlow();
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(_open) => (!_open ? requestClose() : undefined)}>
        <DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] max-w-5xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Importar usuários por CSV</DialogTitle>
            <DialogDescription>
              Importe usuários internos ou clientes sem alterar o cadastro individual.
            </DialogDescription>
          </DialogHeader>

          {(step === 'setup' || step === 'processing') && (
            <BulkImportSetup
              userType={userType}
              selectedBrandIds={selectedBrandIds}
              brandOptions={brandOptions}
              partnersLoading={partnersLoading}
              partnersError={partnersError}
              file={file}
              fileError={fileError}
              setupError={setupError}
              isProcessing={step === 'processing'}
              onUserTypeChange={handleUserTypeChange}
              onBrandIdsChange={setSelectedBrandIds}
              onFileChange={handleFileChange}
              onPreview={handlePreview}
            />
          )}

          {(step === 'review' || step === 'confirming') && (
            <BulkImportReview
              userType={userType}
              fileName={file?.name ?? ''}
              brands={displayedBrands}
              rows={rows}
              summary={summary}
              setupError={setupError}
              isConfirming={step === 'confirming'}
              onBack={goBackToSetup}
              onEdit={setEditingRow}
              onRemove={requestRemoveRow}
              onConfirm={() => setIsConfirmOpen(true)}
            />
          )}

          {step === 'result' && result && (
            <BulkImportResult result={result} onBackToList={closeFlow} onNewImport={startNewImport} />
          )}

          {step === 'confirming' && (
            <div className="sr-only" role="status" aria-live="polite">
              Confirmando importação
            </div>
          )}
        </DialogContent>
      </Dialog>

      <BulkImportEditRow
        row={editingRow}
        userType={userType}
        onClose={() => setEditingRow(null)}
        onSave={handleEditRow}
      />

      <Dialog open={isTypeChangeConfirmOpen} onOpenChange={(_open) => (!_open ? cancelUserTypeChange() : undefined)}>
        <DialogContent className="sm:max-w-[425px] w-[calc(100%-2rem)]">
          <DialogHeader>
            <DialogTitle>Alterar tipo de usuário</DialogTitle>
            <DialogDescription>
              Alterar o tipo de usuário irá limpar o arquivo e os dados importados. Deseja continuar?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={cancelUserTypeChange}>
              Cancelar
            </Button>
            <Button type="button" onClick={confirmUserTypeChange}>
              Continuar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rowPendingRemoval} onOpenChange={(_open) => (!_open ? cancelRemoveRow() : undefined)}>
        <DialogContent className="sm:max-w-[425px] w-[calc(100%-2rem)]">
          <DialogHeader>
            <DialogTitle>Remover linha</DialogTitle>
            <DialogDescription>
              A linha {rowPendingRemoval?.rowNumber} não será enviada na confirmação. Deseja removê-la?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={cancelRemoveRow}>
              Cancelar
            </Button>
            <Button type="button" onClick={confirmRemoveRow}>
              Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <DialogContent className="sm:max-w-[480px] w-[calc(100%-2rem)]">
          <DialogHeader>
            <DialogTitle>Confirmar importação</DialogTitle>
            <DialogDescription>
              {summary.sendableRows} usuários serão enviados para cadastro.
            </DialogDescription>
          </DialogHeader>
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-muted-foreground">Marca</dt>
              <dd className="font-medium text-gray-900">
                {displayedBrands.length > 0
                  ? displayedBrands.map((brand) => brand.name).join(', ')
                  : 'Nenhuma marca selecionada'}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Tipo</dt>
              <dd className="font-medium text-gray-900">
                {getBulkUserTypeLabel(userType)}
              </dd>
            </div>
          </dl>
          <DialogFooter className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setIsConfirmOpen(false)}>
              Cancelar
            </Button>
            <Button type="button" onClick={handleConfirmImport}>
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isLeaveConfirmOpen} onOpenChange={setIsLeaveConfirmOpen}>
        <DialogContent className="sm:max-w-[425px] w-[calc(100%-2rem)]">
          <DialogHeader>
            <DialogTitle>Sair da importação</DialogTitle>
            <DialogDescription>
              Os dados da importação serão perdidos se você sair agora. Deseja continuar?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setIsLeaveConfirmOpen(false)}>
              Cancelar
            </Button>
            <Button type="button" onClick={closeFlow}>
              Sair
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {step === 'processing' && (
        <span className="sr-only">
          <Loader2 className="h-4 w-4 animate-spin" />
        </span>
      )}
    </>
  );
}
